import React, { useState, useCallback } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import base from '../styles/Base.module.css';
import styles from '../styles/pages/Results.module.css';
import DefaultPage from './DefaultPage';
import { ReportingHeader } from 'components/reports/ReportingHeader';
import { ExpandableReport } from 'components/expandable/ExpandableReport';
import {
  useSurveyContext,
  useSurveyDispatchContext,
} from 'contexts/SurveyContext';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import _ from 'lodash';
import { QuestionReportPreview } from 'components/reports/QuestionReportPreview';
import { getQuestionTypeText } from '@ax2/common/model/QuestionType';
import { ActionButton } from '@ax2/common/components/ActionButton';
import Loading from '@ax2/common/components/Loading';
import { exportAsPDF } from 'reports/exports/exportPDF';
import { ExportFn } from 'reports/reportBuilder';
import { QuestionReportExpanded } from 'components/reports/QuestionReportExpanded';
import { exportAsPPTX } from 'reports/exports/exportPPTX';
import QuestionResults from 'model/reports/QuestionResults';
import { getResults } from 'rest/makeSurveyClient';
import {
  exportResultsAsXlsx,
  exportAnswersAsXlsx,
} from 'reports/exports/exportXlsx';
import config from '../config';
import { getMockedSurvey, getMockedSurveyResults } from 'rest/mockedDataClient';
import { useInterval } from 'helpers/hooks/useInterval';

const Results: React.FC<RouteComponentProps> = ({ history }) => {
  const surveySchema = useSurveyContext();
  const surveyDispatch = useSurveyDispatchContext();
  const [inProgress, setInprogress] = useState(false);

  const [reports, setReports] = React.useState<
    { question: QuestionSchema; result?: QuestionResults }[]
  >([]);

  const updateResults = useCallback(async () => {
    if (config.type !== 'demo' && surveySchema) {
      const results = await getResults(surveySchema.id);
      setReports(
        _.map(surveySchema.questions, q => ({
          question: q,
          result: _.find(results, r => r.questionId === q.id),
        }))
      );
    }
  }, [surveySchema]);

  // Update when schema is changed
  React.useEffect(() => {
    updateResults();
  }, [surveyDispatch, surveySchema, updateResults]);

  // Polling update
  useInterval(
    () => {
      updateResults();
    },
    config.pollingInterval,
    false
  );

  // Update for Demo Mode
  React.useEffect(() => {
    async function updateDemoResults() {
      const mSurveySchema = getMockedSurvey();
      surveyDispatch({ type: 'new', survey: mSurveySchema });
      const results = getMockedSurveyResults();
      setReports(
        _.map(mSurveySchema.questions, q => ({
          question: q,
          result: _.find(results, r => r.questionId === q.id),
        }))
      );
    }
    if (config.type === 'demo') {
      updateDemoResults(); // We use this mocked survey & results only in demo mode
    }
  }, [surveyDispatch]);

  const exports: ExportFn[] = [];
  const handleNewExport = (eF: ExportFn, index: number) => {
    exports[index] = eF;
  };

  return (
    <DefaultPage
      header={{
        title: 'Survey Insights',
        subtitle:
          surveySchema && surveySchema.title ? surveySchema.title : undefined,
        backAction: {
          text: 'BACK TO DASHBOARD',
          onCompletion: () => history.push('/dashboard'),
        },
      }}
      wideContent={true}
    >
      <ReportingHeader />
      <div className={`${base.contentContainer} ${styles.resultsContainer}`}>
        <div className={styles.exportContainer}>
          <span className={styles.exportTitle}>Export Report as:</span>
          {inProgress === false && (
            <div className={styles.exportActions}>
              <ActionButton
                text="PDF"
                onClick={async () => {
                  setInprogress(true);
                  await exportAsPDF(
                    _.compact(_.map(exports, e => e())),
                    surveySchema && surveySchema.title ? surveySchema.title : ''
                  );
                  setInprogress(false);
                }}
              />
              <ActionButton
                text="PPTX"
                onClick={async () => {
                  setInprogress(true);
                  await exportAsPPTX(
                    _.compact(_.map(exports, e => e())),
                    surveySchema && surveySchema.title ? surveySchema.title : ''
                  );
                  setInprogress(false);
                }}
              />
              <ActionButton
                text="CHART DATA"
                onClick={async () => {
                  if (surveySchema) {
                    setInprogress(true);
                    await exportResultsAsXlsx(surveySchema);
                    setInprogress(false);
                  }
                }}
              />
              <ActionButton
                text="RAW QUESTION DATA"
                onClick={async () => {
                  if (surveySchema) {
                    setInprogress(true);
                    await exportAnswersAsXlsx(surveySchema);
                    setInprogress(false);
                  }
                }}
              />
            </div>
          )}
        </div>
        {inProgress && <Loading paddingVertical={16} />}
        <div className={styles.expandableChartsContainer}>
          {_.map(reports, (report, index) => {
            const subtitle = getQuestionTypeText(report.question.questionType);
            return (
              <div className={styles.expandableChart} key={index}>
                <ExpandableReport
                  id={'results-report-' + (index + 1)}
                  title={'Q' + (index + 1)}
                  subtitle={subtitle ? subtitle : 'Unknown question type'}
                  previewContent={<QuestionReportPreview {...report} />}
                  expandedContent={
                    report.result ? (
                      <QuestionReportExpanded
                        question={report.question}
                        results={report.result}
                        onNewExport={e => handleNewExport(e, index)}
                      />
                    ) : (
                      <span>No results found</span>
                    )
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </DefaultPage>
  );
};

export default withRouter(Results);
